import { clsx } from "clsx";
import { type FC } from "react";
import { useHover } from "react-aria";
import { shallowEqual, useSelector } from "react-redux";

import styleTrack from "./styles/Track.module.scss";

import { getHspHitRange } from "../../app-utils/blast.js";
import { useSVGTrackTooltip } from "../../app-utils/hooks/useTrackTooltip.js";
import { rangesIntersect } from "../../app-utils/range.js";
import { conditionallyPluralizeTrackLabel } from "../../app-utils/string.js";
import { type IPileup } from "../../app-utils/track-shapes/track-pileup-factory.js";
import { type Range, type Track } from "../../graphql.js";
import { type IAppState } from "../../redux/reducer.js";
import { HspShape } from "../blast-result/HspShape.js";
import { type SetTooltipContent, ShowMessageOverTrack } from "./GenomeViewerTracksList.js";
import { TrackHeader } from "./TrackHeader.js";
import { type TooltipState } from "./features-track/FeatureTrack.js";

interface IBlastHitTrackProps {
  range: Range;
  track: Track;
  trackHeight: number;
  pileup: IPileup;
  noHits: boolean;
  setTooltipContent: SetTooltipContent;
}

export type SvgCallTooltip = (
  interactiveShape: SVGElement,
  targetShape: SVGElement,
  tooltipState: Partial<TooltipState>,
) => void;
/**
 * A visualization component that renders a specific genome browser track within Blast Result display.
 *
 * The component is expected to be used within an existing SVG context and does NOT create its own SVG element. The
 * main input data is a pre-computed pileup of genomic features. During the pileup calculation each genomic feature is
 * assigned view coordinates and a width such that the features don't overlap on the screen.
 *
 * Note that the BlastHitTrack component is currently only supporting gene features and will need to extended to support
 * additional feature types.
 *
 * created on 11/24/2016
 */
const BlastHitTrack: FC<IBlastHitTrackProps> = (props) => {
  const trackWidth = useSelector<IAppState, number>((state) => state.genomeBrowser.layout.trackWidth, shallowEqual);
  const { hoverProps, isHovered } = useHover({});

  const callTooltip = useSVGTrackTooltip(props.setTooltipContent);

  return (
    <div className={"feature-track"}>
      <div className={clsx(styleTrack.track, { [styleTrack.track_hovered]: isHovered })} {...hoverProps}>
        <TrackHeader title={conditionallyPluralizeTrackLabel(props.track)} isTrackHovered={isHovered} />
        <div className={styleTrack.container}>
          <svg
            width="100%"
            height={props.trackHeight}
            key={`canvas-${props.track.name}`}
            style={{ flex: 1, overflow: "visible" }}
          >
            <g className="feature-track-canvas" transform={`translate(0, -0.5)`}>
              {props.noHits ? (
                <foreignObject x="0" y="0" width={"100%"} height={props.trackHeight}>
                  <ShowMessageOverTrack message={"No Hits"} />
                </foreignObject>
              ) : (
                props.pileup?.hsps?.map((pileupHsp) =>
                  rangesIntersect(getHspHitRange(pileupHsp.hsp), props.range) ? (
                    <HspShape
                      pileup={pileupHsp}
                      callTooltip={callTooltip}
                      key={`hsp-${pileupHsp.hit.rank}-${pileupHsp.hsp.rank}-${pileupHsp.width}`}
                    />
                  ) : null,
                )
              )}
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

export { BlastHitTrack };
